import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { AR_TABLE_PREFERENCES, TABLE_PREFERENCES } from '../constants';
import {
  AccountDetails,
  AdvancedFilterPPReport,
  AdvancedFilterWriteBackRetryReport,
  PatientBillingSummaryResp,
  PaymentPlanPatient,
  PreviewPaymentPlan,
} from '../models';
import { TTKeyVaultSecrets } from '../models/setting.model';

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private ttKeyVaultSecrets = new BehaviorSubject(new TTKeyVaultSecrets());
  private accountDetails = new BehaviorSubject(new AccountDetails());
  private practiceId = new BehaviorSubject(0);
  private providerId = new BehaviorSubject(0);
  private patientId = new BehaviorSubject(0);
  private note = new BehaviorSubject('');
  private practiceLogo = new BehaviorSubject('');
  private noteSubject = new BehaviorSubject('');
  private userId = new BehaviorSubject(0);
  private transactionId = new BehaviorSubject(0);
  private isFormSubmitted = new BehaviorSubject(false);
  private isAssessmentFormDirty = new BehaviorSubject(false);
  private isLoadingDefaultAddress = new BehaviorSubject(false);
  private isAccountDetailsSaved = new BehaviorSubject(false);
  private tablePreferenceName = new BehaviorSubject('');
  private paymentType = new BehaviorSubject('');
  private todayAllTableName = new BehaviorSubject(
    TABLE_PREFERENCES.ALL_PATIENT
  );
  private arDetailsTableName = new BehaviorSubject(
    AR_TABLE_PREFERENCES.AR_BY_DOS
  );
  private billingPayments = new BehaviorSubject(
    new PatientBillingSummaryResp()
  );
  private advancedFilters = new BehaviorSubject({});
  anyArray: any = [];
  private previewPaymentPlan = new BehaviorSubject(new PreviewPaymentPlan());
  private selectedPaymentPlan = new BehaviorSubject(new PaymentPlanPatient());
  private selectedPaymentPlanEmis = new BehaviorSubject(this.anyArray);
  private selectedPaymentPlanAdjustementEmis = new BehaviorSubject(
    this.anyArray
  );
  private assessmentToolSelectedMethod = new BehaviorSubject({
    value: '',
    index: 0,
  });
  private advancedFilterWriteBackRetryReport = new BehaviorSubject(
    new AdvancedFilterWriteBackRetryReport()
  );
  private advancedFilterPPReport = new BehaviorSubject(
    new AdvancedFilterPPReport()
  );
  private paymentPlan = new BehaviorSubject({
    paymentPlanAmt: 0,
    todayPaymentAmt: 0,
    remainingPaymentPlanAmt: 0,
    remainingPatientPortion: 0,
  });
  private onDemandSyncStatus = new Subject<{isOnDemandSuccessful: boolean}>;
  private isInstallerInstalled = new BehaviorSubject(false);
  private searchInvited = new BehaviorSubject<string>('');
  private searchReceived = new BehaviorSubject<string>('');
  private pendingPracticeList = new BehaviorSubject<any[]>([]); 
  private userRequestedList = new BehaviorSubject(false); 
  private isFormDirty = new BehaviorSubject(false);
  private isPmsConfigurationchanged = new BehaviorSubject(false);

  getOnDemandSyncStatus(): Observable<any> {
    return this.onDemandSyncStatus.asObservable();
  }

  setOnDemandSyncStatus(isOnDemandSuccessful: boolean)
  {
    this.onDemandSyncStatus.next({isOnDemandSuccessful});
  }

  getIsInstallerInstalled(): Observable<boolean> {
    return this.isInstallerInstalled.asObservable();
  }

  setIsInstallerInstalled(isInstalled: boolean) {
    this.isInstallerInstalled.next(isInstalled);
  }

  setAccountDetails(accountDetails: AccountDetails) {
    this.accountDetails.next(accountDetails);
  }

  getAccountDetails(): Observable<AccountDetails> {
    return this.accountDetails.asObservable();
  }

  setPracticeId(practiceId: number) {
    this.practiceId.next(practiceId);
  }

  getPracticeId(): Observable<number> {
    return this.practiceId.asObservable();
  }

  setPatientId(patientId: number) {
    this.patientId.next(patientId);
  }

  getPatientId(): Observable<number> {
    return this.patientId.asObservable();
  }

  setTransactionId(patientId: number) {
    this.transactionId.next(patientId);
  }

  getTransactionId(): Observable<number> {
    return this.transactionId.asObservable();
  }

  setTransactionIdForPaymenyPlan(patientId: number) {
    this.transactionId.next(patientId);
  }

  gsetTransactionIdForPaymenyPlan(): Observable<number> {
    return this.transactionId.asObservable();
  }

  setProviderId(providerId: number) {
    this.providerId.next(providerId);
  }

  getNoteSubject(): Observable<string> {
    return this.noteSubject.asObservable();
  }

  setNoteSubject(note: string) {
    this.noteSubject.next(note);
  }

  getNote(): Observable<string> {
    return this.note.asObservable();
  }

  setNote(note: string) {
    this.note.next(note);
  }

  getPracticeLogo(): Observable<string> {
    return this.practiceLogo.asObservable();
  }

  setPracticeLogo(practiceLogo: string) {
    this.practiceLogo.next(practiceLogo);
  }

  getProviderId(): Observable<number> {
    return this.providerId.asObservable();
  }

  setUserId(userId: number) {
    this.userId.next(userId);
  }

  getUserId(): Observable<number> {
    return this.userId.asObservable();
  }

  setFormSubmitted(isSubmitted: boolean) {
    this.isFormSubmitted.next(isSubmitted);
  }

  getFormSubmitted(): Observable<boolean> {
    return this.isFormSubmitted.asObservable();
  }

  setIsLoadingDefaultAddress(isLoadingDefaultAddress: boolean) {
    this.isLoadingDefaultAddress.next(isLoadingDefaultAddress);
  }

  getIsLoadingDefaultAddress(): Observable<boolean> {
    return this.isLoadingDefaultAddress.asObservable();
  }

  setTablePreferenceName(tableName: string) {
    this.tablePreferenceName.next(tableName);
  }

  getTablePreferenceName(): Observable<string> {
    return this.tablePreferenceName.asObservable();
  }

  setTodayAllTableName(tableName: string) {
    this.todayAllTableName.next(tableName);
  }

  getTodayAllTableName(): Observable<string> {
    return this.todayAllTableName.asObservable();
  }

  setARDetailsTableName(tableName: string) {
    this.arDetailsTableName.next(tableName);
  }

  getARDetailsTableName(): Observable<string> {
    return this.arDetailsTableName.asObservable();
  }

  setBillingPaymentsData(patientBillingSummary: PatientBillingSummaryResp) {
    this.billingPayments.next(patientBillingSummary);
  }

  getBillingPaymentsData(): Observable<PatientBillingSummaryResp> {
    return this.billingPayments.asObservable();
  }

  setPaymentType(type: string) {
    this.paymentType.next(type);
  }

  getPaymentType(): Observable<string> {
    return this.paymentType.asObservable();
  }

  setPaymentPlanValues(value: any) {
    this.paymentPlan.next(value);
  }

  getPaymentPlanValues() {
    return this.paymentPlan.asObservable();
  }

  setSelectedPaymentPlanValues(value: any) {
    this.selectedPaymentPlan.next(value);
  }

  getSelectedPaymentPlanValues() {
    return this.selectedPaymentPlan.asObservable();
  }

  setSelectedPaymentPlanEmisValues(value: any) {
    this.selectedPaymentPlanEmis.next(value);
  }

  getSelectedPaymentPlanEmisValues() {
    return this.selectedPaymentPlanEmis.asObservable();
  }

  setSelectedPaymentPlanAdjustementEmisValues(value: any) {
    this.selectedPaymentPlanAdjustementEmis.next(value);
  }

  getSelectedPaymentPlanAdjustementEmisValues() {
    return this.selectedPaymentPlanAdjustementEmis.asObservable();
  }

  setPreviewPaymentPlanValues(value: any) {
    this.previewPaymentPlan.next(value);
  }

  getPreviewPaymentPlanValues() {
    return this.previewPaymentPlan.asObservable();
  }

  setAdvancedFiltersValues(value: any) {
    this.advancedFilters.next(value);
  }

  getAdvancedFiltersValues() {
    return this.advancedFilters.asObservable();
  }

  setAssessmentToolSelectedMethodValues(value: string, index: number) {
    this.assessmentToolSelectedMethod.next({ value, index });
  }

  getAssessmentToolSelectedMethodValues() {
    return this.assessmentToolSelectedMethod.asObservable();
  }

  setAdvancedFilterWriteBackRetryReport(value: any) {
    this.advancedFilterWriteBackRetryReport.next(value);
  }

  getAdvancedFilterWriteBackRetryReport() {
    return this.advancedFilterWriteBackRetryReport.asObservable();
  }

  setAdvancedFilterPPReport(value: any) {
    this.advancedFilterPPReport.next(value);
  }

  getAdvancedFilterPPReport() {
    return this.advancedFilterPPReport.asObservable();
  }

  setFormDirty(isSubmitted: boolean) {
    this.isAssessmentFormDirty.next(isSubmitted);
  }

  getAssessmentFormDirty(): Observable<boolean> {
    return this.isAssessmentFormDirty.asObservable();
  }

  setIsAccountDetailsSaved(isAccountDetailsSaved: boolean) {
    this.isAccountDetailsSaved.next(isAccountDetailsSaved);
  }

  getIsAccountDetailsSaved(): Observable<boolean> {
    return this.isAccountDetailsSaved.asObservable();
  }

  setSecretsValue(secrets: TTKeyVaultSecrets) {
    this.ttKeyVaultSecrets.next(secrets);
  }

  getSecretsValue(): Observable<TTKeyVaultSecrets> {
    return this.ttKeyVaultSecrets.asObservable();
  }
  setInvitedSearchTerm(term: string) {
    this.searchInvited.next(term);
  }

  getInvitedSearchTerm(): Observable<string> {
    return this.searchInvited.asObservable();
  }
  setReceivedSearchTerm(term: string) {
    this.searchReceived.next(term);
  }

  getReceivedSearchTerm(): Observable<string> {
    return this.searchReceived.asObservable();
  }
  setPendingLoginData(practiceList: any[]) {
    this.pendingPracticeList.next(practiceList);
  }
  getPendingLoginData() {
    return {
      practiceList: this.pendingPracticeList.getValue(),
    };
  }
  hasPendingLoginData(): boolean {
    return this.pendingPracticeList.getValue().length > 0;
  }

  clearPendingLoginData() {
    this.pendingPracticeList.next([]);
  }
  setUserRequestedList(isUserRequestedList: boolean) {
    this.userRequestedList.next(isUserRequestedList);
  }

  getUserRequestedList(): Observable<boolean> {
    return this.userRequestedList.asObservable();
  }

  getIsFormDirty(): Observable<boolean> {
    return this.isFormDirty.asObservable();
  }

  setIsFormDirty(isDirty: boolean) {
    this.isFormDirty.next(isDirty);
  }

  getIsFormDirtyValue() {
    return this.isFormDirty.getValue();
  }

  getIsPmsConfigurationchanged(): Observable<boolean> {
    return this.isPmsConfigurationchanged.asObservable();
  }

  setIsPmsConfigurationchanged(isChanged: boolean) {
    this.isPmsConfigurationchanged.next(isChanged);
  }
}
